import type { ChangeEvent, DragEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useToast } from "@/components/ToastProvider";
import { cn } from "@/lib/utils";
import { Download, Layers, Palette, Save, Send, Sparkles, UploadCloud } from "lucide-react";
import { CTA_PRESETS, SHORTFORM_EFFECTS, SHORTFORM_FILTERS, SHORTFORM_FONTS, SHORTFORM_TEMPLATES } from "@/lib/marketingConstants";
import type { ComposerState } from "@/pages/MarketingPage";

/**
 * Renders the Short-Form Builder experience, handling template selection, clip uploads, visual tweaks,
 * exports, and shipping the layout into the composer via the passed `onSendToComposer` callback.
 */
export function ShortFormGeneratorView({ onSendToComposer }: { onSendToComposer: (options?: Partial<ComposerState>) => void }) {
  const { push } = useToast();
  const [templateId, setTemplateId] = useState<string>(SHORTFORM_TEMPLATES[0].id);
  const [title, setTitle] = useState("Desert Bloom Sessions");
  const [subtitle, setSubtitle] = useState("Live in Topanga • May 10");
  const [ctaPreset, setCtaPreset] = useState<string>(CTA_PRESETS[0].id);
  const [ctaText, setCtaText] = useState<string>(CTA_PRESETS[0].label);
  const [fontFamily, setFontFamily] = useState<string>(SHORTFORM_FONTS[0]);
  const [fontSize, setFontSize] = useState<number>(46);
  const [primaryColor, setPrimaryColor] = useState("#fefcf5");
  const [accentColor, setAccentColor] = useState("#f97316");
  const [effects, setEffects] = useState<string[]>(["fade"]);
  const [filters, setFilters] = useState<string[]>(["grain"]);
  const [autoFit, setAutoFit] = useState(true);
  const [selectedLayer, setSelectedLayer] = useState("clip");
  const [clipAsset, setClipAsset] = useState<{ url: string; name: string; kind: "video" | "image" } | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [exportStatus, setExportStatus] = useState<"idle" | "rendering" | "ready">("idle");
  const [exportUrl, setExportUrl] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const selectedTemplate =
    SHORTFORM_TEMPLATES.find((preset) => preset.id === templateId) ?? SHORTFORM_TEMPLATES[0];

  const attachClip = useCallback(
    (file: File) => {
      const url = URL.createObjectURL(file);
      setClipAsset((previous) => {
        if (previous?.url) {
          URL.revokeObjectURL(previous.url);
        }
        return {
          url,
          name: file.name,
          kind: file.type.startsWith("video") ? "video" : "image",
        };
      });
      push({ title: "Clip ready", message: `${file.name} attached to the canvas.` });
    },
    [push],
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        attachClip(file);
      }
      event.target.value = "";
    },
    [attachClip],
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragOver(false);
      const file = event.dataTransfer.files?.[0];
      if (file) {
        attachClip(file);
      }
    },
    [attachClip],
  );

  const layers = useMemo(
    () => [
      {
        id: "clip",
        label: clipAsset ? clipAsset.name : "Clip layer",
        description: clipAsset ? "Drag to reposition or replace with a still." : "Drop a clip or still image.",
      },
      { id: "title", label: "Title layer", description: title || "Add a hero title." },
      { id: "subtitle", label: "Subtitle layer", description: subtitle || "Add context or tour stop." },
      { id: "cta", label: "CTA overlay", description: ctaText || "Drive next action." },
    ],
    [clipAsset, title, subtitle, ctaText],
  );

  useEffect(() => {
    return () => {
      if (clipAsset?.url) {
        URL.revokeObjectURL(clipAsset.url);
      }
      if (exportUrl) {
        URL.revokeObjectURL(exportUrl);
      }
    };
  }, [clipAsset, exportUrl]);

  useEffect(() => {
    setAccentColor(selectedTemplate.accent);
  }, [selectedTemplate.accent]);

  const buildPreview = useCallback(async () => {
    setExportStatus("rendering");
    await new Promise((resolve) => setTimeout(resolve, 900));
    const payload = {
      templateId,
      title,
      subtitle,
      ctaText,
      fontFamily,
      fontSize,
      autoFit,
      filters,
      effects,
      accentColor,
      primaryColor,
      clip: clipAsset?.name ?? null,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    setExportUrl((previous) => {
      if (previous) {
        URL.revokeObjectURL(previous);
      }
      return url;
    });
    setExportStatus("ready");
    return url;
  }, [
    accentColor,
    autoFit,
    clipAsset?.name,
    ctaText,
    effects,
    filters,
    fontFamily,
    fontSize,
    primaryColor,
    subtitle,
    templateId,
    title,
  ]);

  const handleExport = useCallback(async () => {
    const url = await buildPreview();
    const timestamp = new Date().toISOString();
    setLastSavedAt(timestamp);
    push({ title: "Render ready", message: "Placeholder export created – safe to download or share." });
    return url;
  }, [buildPreview, push]);

  const handleSend = useCallback(async () => {
    const url = exportUrl ?? (await buildPreview());
    onSendToComposer({
      initialAsset: {
        id: `shortform-${Date.now()}`,
        type: "template",
        title,
        url,
        thumbnailUrl: url,
      },
      initialDatetime: new Date().toISOString(),
    });
    setLastSavedAt(new Date().toISOString());
    push({ title: "Composer staged", message: "Layout sent to the Post Composer drawer." });
  }, [buildPreview, exportUrl, onSendToComposer, push, title]);

  const handleSaveAsset = useCallback(async () => {
    await buildPreview();
    setLastSavedAt(new Date().toISOString());
    push({ title: "Asset saved", message: "Saved as a draft asset for future exports." });
  }, [buildPreview, push]);

  const handleCtaPresetChange = (value: string) => {
    setCtaPreset(value);
    if (value === "custom") {
      return;
    }
    const preset = CTA_PRESETS.find((entry) => entry.id === value);
    if (preset) {
      setCtaText(preset.label);
    }
  };

  const handleCtaTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCtaText(value);
    if (!CTA_PRESETS.some((entry) => entry.label === value)) {
      setCtaPreset("custom");
    }
  };

  const presetDescription = selectedTemplate.description;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-semibold text-foreground">Short-Form Builder</h2>
          <p className="text-sm text-muted-foreground">
            Plan template-driven Reels/TikTok layouts, drop in clips, and simulate CTA overlays before exporting.
          </p>
        </div>
        <Badge className="border border-success/50 bg-success/10 text-success">9:16 Canvas</Badge>
      </div>

      <section className="space-y-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
          <Sparkles className="h-4 w-4 text-primary" />
          Template selector
          <span className="text-muted-foreground">({presetDescription})</span>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-2">
          {SHORTFORM_TEMPLATES.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => setTemplateId(preset.id)}
              className={cn(
                "w-44 shrink-0 rounded-2xl border-2 p-3 text-left text-sm text-foreground transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400",
                templateId === preset.id ? "border-primary shadow-lg shadow-primary/20" : "border-border/80",
              )}
            >
              <div className={cn("h-24 rounded-xl bg-gradient-to-br", preset.gradientClass)} />
              <p className="mt-3 font-semibold">{preset.label}</p>
              <p className="text-xs text-muted-foreground">{preset.description}</p>
            </button>
          ))}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <Card className="border-border/80 bg-background/50 p-6">
          <div className="flex flex-col gap-6 lg:flex-row">
            <div className="flex flex-1 justify-center">
              <div
                className={cn(
                  "relative aspect-[9/16] w-full max-w-[360px] overflow-hidden rounded-[40px] border bg-background/60 shadow-2xl transition",
                  isDragOver ? "border-success/70 ring-4 ring-success/30" : "border-border/10",
                )}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={(event) => {
                  event.preventDefault();
                  setIsDragOver(false);
                }}
                onDrop={handleDrop}
              >
                <div className={cn("absolute inset-0 rounded-[36px] bg-gradient-to-br", selectedTemplate.gradientClass)} />
                {selectedTemplate.overlayClass ? (
                  <div className={cn("absolute inset-0 rounded-[36px]", selectedTemplate.overlayClass)} />
                ) : null}
                {clipAsset ? (
                  clipAsset.kind === "video" ? (
                    <video
                      src={clipAsset.url}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className={cn(
                        "absolute inset-3 h-[calc(100%-1.5rem)] w-[calc(100%-1.5rem)] rounded-[32px] object-cover",
                        autoFit ? "object-cover" : "object-contain bg-black/50",
                        selectedLayer === "clip" && "ring-2 ring-primary/70",
                      )}
                    />
                  ) : (
                    <img
                      src={clipAsset.url}
                      alt={clipAsset.name}
                      className={cn(
                        "absolute inset-3 h-[calc(100%-1.5rem)] w-[calc(100%-1.5rem)] rounded-[32px] object-cover",
                        autoFit ? "object-cover" : "object-contain bg-black/50",
                        selectedLayer === "clip" && "ring-2 ring-primary/70",
                      )}
                    />
                  )
                ) : (
                  <div className="absolute inset-6 flex flex-col items-center justify-center rounded-[30px] border border-dashed border-border/25 text-center">
                    <UploadCloud className="mb-2 h-10 w-10 text-foreground/80" />
                    <p className="text-sm text-foreground">Drop clip or image</p>
                    <p className="text-xs text-muted-foreground">9:16 recommended • MOV/MP4/PNG</p>
                  </div>
                )}
                <div className="pointer-events-none absolute inset-5 rounded-[32px] border border-border/10" />
                <div className="pointer-events-none absolute inset-8">
                  <div className="absolute inset-x-4 top-1/2 h-px -translate-y-1/2 bg-background/10" />
                  <div className="absolute inset-y-4 left-1/2 w-px -translate-x-1/2 bg-background/10" />
                </div>
                <div className="pointer-events-none absolute inset-5">
                  {["top-5 left-5", "top-5 right-5", "bottom-5 left-5", "bottom-5 right-5"].map((position) => (
                    <span
                      key={position}
                      className={cn(
                        "absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-border/70 bg-background/40",
                        position,
                      )}
                    />
                  ))}
                  <span className="absolute left-1/2 -top-3 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border border-border/40 bg-background/40 text-[10px] uppercase text-foreground/70">
                    ↻
                  </span>
                </div>
                <div
                  className={cn(
                    "absolute inset-x-8 top-12 rounded-3xl px-2 text-center transition",
                    (selectedLayer === "title" || selectedLayer === "subtitle") && "ring-2 ring-primary/70",
                  )}
                  style={{ fontFamily, color: primaryColor }}
                >
                  <p className="text-3xl font-semibold leading-tight md:text-4xl" style={{ fontSize }}>
                    {title || "Add a headline"}
                  </p>
                  <p className="mt-2 text-sm uppercase tracking-wide text-foreground/80">{subtitle || "Add a subtitle"}</p>
                </div>
                <div
                  className={cn(
                    "absolute bottom-10 left-1/2 w-[75%] -translate-x-1/2 rounded-full px-6 py-3 text-center text-sm font-semibold uppercase tracking-wide transition",
                    selectedLayer === "cta" && "ring-2 ring-primary/70",
                  )}
                  style={{ background: accentColor, color: "#0f172a", fontFamily }}
                >
                  {ctaText || "CTA goes here"}
                </div>
                <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                  <Button
                    type="button"
                    size="sm"
                    className="pointer-events-auto bg-background/20 text-foreground hover:bg-background/30"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Replace clip
                  </Button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*,image/*"
                  className="sr-only"
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="flex w-full max-w-sm flex-col gap-4">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Title layer
                <Input
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  className="mt-1 bg-background/40 text-foreground"
                  placeholder="New single out Friday"
                />
              </label>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Subtitle
                <Input
                  value={subtitle}
                  onChange={(event) => setSubtitle(event.target.value)}
                  className="mt-1 bg-background/40 text-foreground"
                  placeholder="Live in Denver • 07.18"
                />
              </label>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">CTA Overlay</p>
                <Select value={ctaPreset} onValueChange={handleCtaPresetChange}>
                  <SelectTrigger className="bg-background/40 text-foreground">
                    <SelectValue placeholder="Select preset" />
                  </SelectTrigger>
                  <SelectContent className="bg-background/40 text-foreground">
                    {CTA_PRESETS.map((preset) => (
                      <SelectItem key={preset.id} value={preset.id}>
                        {preset.label}
                      </SelectItem>
                    ))}
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={ctaText}
                  onChange={handleCtaTextChange}
                  className="bg-background/40 text-foreground"
                  placeholder="CTA text"
                />
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-border/80 bg-background/70 px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-foreground">Auto-fit layers</p>
                  <p className="text-xs text-muted-foreground">Auto-size clips & text to frame bounds.</p>
                </div>
                <Switch checked={autoFit} onCheckedChange={(checked) => setAutoFit(Boolean(checked))} />
              </div>
            </div>
          </div>
        </Card>
        <div className="space-y-4">
          <Card className="border-border/80 bg-background/60 p-4">
            <div className="flex items-center gap-2 text-foreground">
              <Layers className="h-4 w-4 text-primary" />
              <div>
                <p className="text-sm font-semibold">Layers panel</p>
                <p className="text-xs text-muted-foreground">Enable, reorder, and target layers for transform handles.</p>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {layers.map((layer) => (
                <button
                  key={layer.id}
                  type="button"
                  onClick={() => setSelectedLayer(layer.id)}
                  className={cn(
                    "w-full rounded-2xl border px-4 py-3 text-left transition",
                    selectedLayer === layer.id
                      ? "border-primary/60 bg-primary/10 text-foreground"
                      : "border-border/80 text-foreground/70 hover:border-border hover:text-foreground",
                  )}
                >
                  <p className="text-sm font-semibold">{layer.label}</p>
                  <p className="text-xs text-muted-foreground">{layer.description}</p>
                </button>
              ))}
            </div>
          </Card>
          <Card className="border-border/80 bg-background/60 p-4">
            <div className="flex items-center gap-2 text-foreground">
              <Palette className="h-4 w-4 text-info" />
              <div>
                <p className="text-sm font-semibold">Controls drawer</p>
                <p className="text-xs text-muted-foreground">Typography, palettes, entry effects, and filters.</p>
              </div>
            </div>
            <div className="mt-4 space-y-4 text-sm text-foreground">
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Font family</p>
                <Select value={fontFamily} onValueChange={(value) => setFontFamily(value)}>
                  <SelectTrigger className="bg-background/40 text-foreground">
                    <SelectValue placeholder="Select font" />
                  </SelectTrigger>
                  <SelectContent className="bg-background/40 text-foreground">
                    {SHORTFORM_FONTS.map((font) => (
                      <SelectItem key={font} value={font}>
                        {font}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Font size</p>
                <Slider
                  value={[fontSize]}
                  min={24}
                  max={72}
                  step={2}
                  onValueChange={([value]) => setFontSize(value ?? fontSize)}
                />
                <p className="text-xs text-muted-foreground">{fontSize}px</p>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Primary color
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(event) => setPrimaryColor(event.target.value)}
                    className="mt-1 h-10 w-full cursor-pointer rounded-lg border border-border bg-background/40"
                    aria-label="Primary color"
                  />
                </label>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  CTA color
                  <input
                    type="color"
                    value={accentColor}
                    onChange={(event) => setAccentColor(event.target.value)}
                    className="mt-1 h-10 w-full cursor-pointer rounded-lg border border-border bg-background/40"
                    aria-label="CTA color"
                  />
                </label>
              </div>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Entry effects</p>
                <ToggleGroup
                  type="multiple"
                  className="flex flex-wrap gap-2"
                  value={effects}
                  onValueChange={(value) => setEffects(value as string[])}
                >
                  {SHORTFORM_EFFECTS.map((effect) => (
                    <ToggleGroupItem key={effect.id} value={effect.id} className="text-xs">
                      {effect.label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Background filters</p>
                <ToggleGroup
                  type="multiple"
                  className="flex flex-wrap gap-2"
                  value={filters}
                  onValueChange={(value) => setFilters(value as string[])}
                >
                  {SHORTFORM_FILTERS.map((filter) => (
                    <ToggleGroupItem key={filter.id} value={filter.id} className="text-xs">
                      {filter.label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className="flex flex-col gap-4 rounded-2xl border border-border/80 bg-background/70 p-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-base font-semibold text-foreground">Export control bar</p>
          <p className="text-sm text-muted-foreground">
            {exportStatus === "rendering"
              ? "Rendering preview..."
              : exportStatus === "ready"
                ? "Preview ready – safe to export or send to Composer."
                : "No render yet. Generate a placeholder export to unlock Composer actions."}
            {lastSavedAt ? ` Saved ${new Date(lastSavedAt).toLocaleTimeString()}.` : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            className="border-border/80 text-foreground hover:bg-muted"
            disabled={exportStatus === "rendering"}
            onClick={() => void handleExport()}
          >
            <Download className="mr-2 h-4 w-4" />
            Render / Export
          </Button>
          <Button
            className="bg-success/20 text-success hover:bg-success/30"
            disabled={exportStatus === "rendering"}
            onClick={() => void handleSend()}
          >
            <Send className="mr-2 h-4 w-4" />
            Send to Post Composer
          </Button>
          <Button variant="ghost" className="text-foreground/80 hover:bg-muted/70" onClick={() => void handleSaveAsset()}>
            <Save className="mr-2 h-4 w-4" />
            Save as Asset
          </Button>
        </div>
      </div>
    </div>
  );
}
